import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Spinner from "../components/Spinner";

const TYPE_LABELS = {
  "grid-guide": "Grid Guide",
  listicle: "Ranked Listicle",
};

export default function PageList() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest"); // "newest" | "az"

  const fetchPages = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_BASE_URL}/pages/all`)
      .then((res) => res.json())
      .then((data) => {
        setPages(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handleDelete = async (slug) => {
    if (window.confirm("Are you sure you want to delete this page?")) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/pages/${slug}`, {
          method: "DELETE",
        });
        if (response.ok) {
          alert("Page deleted successfully!");
          fetchPages();
        } else {
          alert("Failed to delete page.");
        }
      } catch (error) {
        console.error("Error deleting page:", error);
        alert("Error deleting page.");
      }
    }
  };

  const visiblePages = pages
    .filter((page) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (page.title || "").toLowerCase().includes(searchLower) ||
        (page.slug || "").toLowerCase().includes(searchLower) ||
        (page.type || "").toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      if (sortBy === "az") {
        return (a.title || "").localeCompare(b.title || "");
      }
      // newest first
      return (b.timestamp || 0) - (a.timestamp || 0);
    });

  return (
    <div className="p-6 h-screen overflow-y-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Select a Page to Edit</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 p-3 shadow-sm outline-none"
          >
            <option value="newest">Sort: Newest first</option>
            <option value="az">Sort: A–Z (title)</option>
          </select>
          <div className="relative w-full sm:w-80">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                aria-hidden="true"
                className="w-5 h-5 text-gray-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </div>
            <input
              type="text"
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-3 shadow-sm transition-shadow outline-none"
              placeholder="Search by title, slug, type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto bg-white shadow-md rounded-lg">
          <table className="min-w-full leading-normal">
            <thead>
              <tr>
                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {visiblePages.map((page) => (
                <tr key={page.slug} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-5 border-b border-gray-200 text-sm font-medium text-gray-800">
                    {page.title || "(No Title)"}
                  </td>
                  <td className="px-5 py-5 border-b border-gray-200 text-sm text-gray-600">
                    <span className="font-bold text-gray-800">
                      {TYPE_LABELS[page.type] || page.type}
                    </span>
                  </td>
                  <td className="px-5 py-5 border-b border-gray-200 text-sm text-gray-600">
                    <a
                      href={`/stores/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      /stores/{page.slug}
                    </a>
                  </td>
                  <td className="px-5 py-5 border-b border-gray-200 text-sm text-gray-600">
                    {page.date}
                  </td>
                  <td className="px-5 py-5 border-b border-gray-200 text-sm font-medium">
                    <div className="flex gap-2">
                      <Link
                        to={`/edit-page/${page.slug}`}
                        className="text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded shadow"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(page.slug)}
                        className="text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded shadow"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {visiblePages.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center text-gray-500"
                  >
                    {pages.length === 0
                      ? "No pages found. Create one first!"
                      : "No pages match your search."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
